import React, { createContext, useContext, useState, useEffect } from 'react';

const CustomerAuthContext = createContext();

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [hasPriceAccess, setHasPriceAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('customerToken');
    if (!token || token.trim() === '') {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/customers/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomer(data.customer);
        setHasPriceAccess(data.customer.permission_status === 'approved');
      } else {
        // Token is invalid or expired, clear it silently
        localStorage.removeItem('customerToken');
        setCustomer(null);
        setHasPriceAccess(false);
      }
    } catch (error) {
      // Network error or other issues, clear token silently
      console.error('Customer auth check failed:', error);
      localStorage.removeItem('customerToken');
      setCustomer(null);
      setHasPriceAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch('/api/customers/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('customerToken', data.token);
        setCustomer(data.customer);
        setHasPriceAccess(data.customer.permissionStatus === 'approved');
        return { success: true, data };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch('/api/customers/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('customerToken');
      if (token) {
        await fetch('/api/customers/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('customerToken');
      setCustomer(null);
      setHasPriceAccess(false);
    }
  };

  const requestPermission = async (requestType, notes) => {
    try {
      const token = localStorage.getItem('customerToken');
      const response = await fetch('/api/customers/request-permission', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ requestType, notes })
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const value = {
    customer,
    hasPriceAccess,
    loading,
    login,
    register,
    logout,
    requestPermission,
    checkAuthStatus
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
